import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Anchor, Facebook } from "lucide-react";

const QUICK_LINKS = [
  { name: "Home", path: "/" },
  { name: "Menu", path: "/menu" },
  { name: "Book Seaside Bites", path: "/book" },
  { name: "Contact", path: "/contact" },
];

const WaveDivider = () => (
  <div className="relative h-16 -mt-1">
    <svg
      viewBox="0 0 1440 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute bottom-0 w-full h-full"
      preserveAspectRatio="none"
    >
      <path
        d="M0 50L48 45.8C96 41.7 192 33.3 288 33.3C384 33.3 480 41.7 576 50C672 58.3 768 66.7 864 62.5C960 58.3 1056 41.7 1152 37.5C1248 33.3 1344 41.7 1392 45.8L1440 50V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0V50Z"
        fill="#112A46"
      />
    </svg>
  </div>
);

const BrandSection = () => (
  <div className="space-y-4">
    <div className="flex items-center gap-2">
      <Anchor className="w-8 h-8 text-seaside-magenta" />
      <span className="font-heading font-bold text-2xl">Seaside Bites</span>
    </div>
    <p className="font-body text-gray-300 leading-relaxed">
      Your go-to spot on the water for fresh, made-to-order favorites in
      Pasco County, Florida.
    </p>
    <a
      href="https://www.facebook.com/share/1DrweEa7SG/"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="footer-facebook-link"
      className="inline-flex items-center gap-2 text-seaside-teal hover:text-seaside-gold transition-colors duration-300"
    >
      <Facebook className="w-5 h-5" />
      <span className="font-body font-semibold">Follow us on Facebook</span>
    </a>
  </div>
);

const QuickLinksSection = () => (
  <div className="space-y-4">
    <h3 className="font-heading font-bold text-xl text-seaside-gold">
      Quick Links
    </h3>
    <nav className="space-y-3">
      {QUICK_LINKS.map((link) => (
        <Link
          key={link.path}
          to={link.path}
          data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
          className="block font-body text-gray-300 hover:text-seaside-magenta transition-colors duration-300"
        >
          {link.name}
        </Link>
      ))}
    </nav>
  </div>
);

const ContactSection = () => (
  <div className="space-y-4">
    <h3 className="font-heading font-bold text-xl text-seaside-gold">
      Contact Us
    </h3>
    <div className="space-y-3">
      <a
        href="tel:727-833-9500"
        data-testid="footer-phone"
        className="flex items-center gap-3 font-body text-gray-300 hover:text-seaside-magenta transition-colors duration-300"
      >
        <Phone className="w-5 h-5 text-seaside-teal" />
        <span>727-833-9500</span>
      </a>
      <a
        href="mailto:seasidebitespasco@gmail.com"
        data-testid="footer-email"
        className="flex items-center gap-3 font-body text-gray-300 hover:text-seaside-magenta transition-colors duration-300"
      >
        <Mail className="w-5 h-5 text-seaside-teal" />
        <span>seasidebitespasco@gmail.com</span>
      </a>
      <div className="flex items-center gap-3 font-body text-gray-300">
        <MapPin className="w-5 h-5 text-seaside-teal flex-shrink-0" />
        <span>Pasco County, Florida</span>
      </div>
    </div>
  </div>
);

const FindUsSection = () => (
  <div className="space-y-4">
    <h3 className="font-heading font-bold text-xl text-seaside-gold">
      Find Us On The Water
    </h3>
    <p className="font-body text-gray-300 leading-relaxed">
      Servicing the surrounding islands of Pasco County. Whether you're
      anchored offshore or hanging out on the sandbar, come find us!
    </p>
    <Link
      to="/book"
      data-testid="footer-book-cta"
      className="inline-block px-6 py-3 bg-seaside-magenta text-white font-body font-bold rounded-full hover:bg-seaside-magenta-hover transition-colors duration-300"
    >
      Book Us Today
    </Link>
  </div>
);

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-seaside-navy text-white">
      <WaveDivider />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <BrandSection />
          <QuickLinksSection />
          <ContactSection />
          <FindUsSection />
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="font-body text-gray-400">
            © {new Date().getFullYear()} Seaside Bites On The Water. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
